import { Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { ConversationService } from 'src/app/services/conversation.service';
import { CreateConversationComponent } from 'src/app/shared/components/create-conversation/create-conversation.component';
import { Conversation } from 'src/app/shared/models/conversation.model';
import { Project } from 'src/app/shared/models/project.model';

@Component({
    selector: 'app-project-conversation',
    templateUrl: './project-conversation.component.html',
    styleUrl: './project-conversation.component.scss',
    standalone: false
})
export class ProjectConversationComponent implements OnInit, OnDestroy {
	@ViewChild('createConversationModal') createConversationModal!: CreateConversationComponent;
	@ViewChild('successModal') successModal!: TemplateRef<any>;

	@Input() currentProject!: Project;
	private searchSubject = new Subject<string>();
	private refreshSubscription!: Subscription;
	reanalyzeModalRef: any;
	deleteModalRef: any;

	listStatus: any[] = [
		{ name: 'WAITING', status_color: 'warning'},
		{ name: 'PAUSED', status_color: 'warning'},
		{ name: 'STARTED', status_color: 'info'},
		{ name: 'ANALYZING', status_color: 'info'},
		{ name: 'COMPLETED', status_color: 'success'},
		{ name: 'FAILED', status_color: 'danger'}
	];

	submitted = false;
	spinner = false;
	success = false;
	successMessage = '';
	message = '';
	title = '';

	conversations: Conversation[] = [];
	selectedConversation!: Conversation;
  currentPage: number = 1;
  pageSize: number = 8;
  totalConversations: number = 0;
  searchText: string = '';
  sortColumn: string = 'createdAt';
  sortDirection: string = 'desc';

	startIndex: number = 0;
  endIndex: number = 0;

	loading = true;
	listloading = true;
	

	currentUser: any = null;
	current_client: any = null;
	colspan = 0;

	isAllow = false;
	allowSpecial = false;
	isOwner = false;
	isEditor = false;
	isAnalyst = false;
	allowedRoles = ['OWNER', 'EDITOR', 'ANALYST', 'RESTRICTED'];

	model_name: string | null = null;

	block_access = false;

	models = ['skin', 'bl', 'voice', 'emotion', 'head-pose', 'leaks'];
	
	constructor(private conversationService: ConversationService, private authService: AuthService, private modalService: NgbModal) {}

	ngOnInit(): void {
		this.currentUser = this.authService.currentUserValue;
		this.current_client = this.authService.currentClientValue;

		this.isOwner = this.current_client.roles.includes('OWNER');
		this.isEditor = this.current_client.roles.includes('EDITOR');
		this.isAnalyst = this.current_client.roles.includes('ANALYST');
		this.isAllow = this.current_client.roles.some((role: string) => this.allowedRoles.includes(role));
		this.allowSpecial = this.current_client.roles.includes('RESTRICTED');

		if (this.isAllow) {
			if (!this.authService.currentClientValue.plan) {
				this.isAllow = false;
			}
		}

		this.colspan = this.currentProject ? this.isAllow ? 10 : 9 : this.isAllow ? 11 : 10;

		this.getConversations();

		this.refreshSubscription = this.conversationService.getRefreshObservable().subscribe(() => {
      this.getConversations();
    });

		// Subscribe to search input changes with debounce
    this.searchSubject.pipe(
      debounceTime(300), // Adjust debounce time as needed
      distinctUntilChanged()
    ).subscribe(() => {
      this.currentPage = 1; // Reset to first page when search text changes
      this.getConversations();
    });
	}

	ngOnDestroy() {
    this.refreshSubscription.unsubscribe();
  }

	getStatusColor(status: string) {
		const s = this.listStatus.find(item => item.name === status);
		return s.status_color;
	}

	getAnalyzingPrecent(ai_models: any) {
		const active_models = Object.values(ai_models).filter((obj: any) => obj.active);
		const done_models = Object.values(active_models).filter((obj: any) => obj.is_done || obj.is_error).length;
		return `${((done_models/active_models.length) * 100).toFixed(0)}%`;
	}

	getConversations() {
		this.loading = true;
    const params = {
      page: this.currentPage.toString(),
      pageSize: this.pageSize.toString(),
      searchText: this.searchText,
      sortColumn: this.sortColumn,
      sortDirection: this.sortDirection
    };


		if (this.currentProject) {
			this.conversationService.getProjectConversations(this.currentProject.id, params).subscribe({
				next: (response: any) => {
					this.conversations = response.conversations;
					this.totalConversations = response.totalConversations;
					this.calculateIndexes();
					this.loading = false;
				},
				error: (err: any) => {
					this.authService.toastErrorMessage(err.message ? err.message : err, 'Project Conversations');
				}
			});
		} else {
			this.conversationService.getConversations(params).subscribe({
				next: (response: any) => {
					this.conversations = response.conversations;
					this.totalConversations = response.totalConversations;
					this.calculateIndexes();
					this.loading = false;
				},
				error: (err) => {
					this.authService.toastErrorMessage(err.message ? err.message : err, 'All Conversations');
				}
			});
		}
  }

	calculateIndexes() {
    this.startIndex = (this.currentPage - 1) * this.pageSize + 1;
    this.endIndex = Math.min(this.currentPage * this.pageSize, this.totalConversations);
  }

	onSearch() {
    this.searchSubject.next(this.searchText);
  }

	onSort(column: string) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.getConversations();
  }

	refreshConversations() {
		this.currentPage = 1;
		this.searchText = '';
		this.getConversations();
	}

	openReAnalayzeModals(conversation: Conversation, content: any) {
		this.selectedConversation = conversation;

		this.reanalyzeModalRef = this.modalService.open(content, { size: 'l', backdrop: 'static', keyboard : false });
	}

	closeModal() {
		if (this.success) {
			this.refreshConversations();
			this.modalService.dismissAll();
		} else {
			this.modalService.dismissAll();
		}
	}

	openCreateConversationModal() {
		this.createConversationModal.openModal(this.currentProject.id);
	}

	openDeleteConversationModal(conversation: Conversation, content: any) {
		this.selectedConversation = conversation;
		this.deleteModalRef = this.modalService.open(content, { size: 'l', backdrop: 'static', keyboard : false });
	}

	deleteConversation() {
		this.loading = true;
		this.submitted = true;
		this.spinner = true;

		this.conversationService.deleteConversation(this.selectedConversation.id).subscribe({
			next: (res) => {
				this.success = true;
				this.title = 'Delete Conversation Success.';
				this.submitted = false;
				this.spinner = false;
				this.successMessage = res.message;
				this.deleteModalRef.close();
				this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });
			},
			error: (err) => {
				this.success = false;
				this.title = 'Delete Conversation Error.';
				this.submitted = false;
				this.spinner = false;
				this.successMessage = err.message ? err.message : err;
				this.deleteModalRef.close();
				this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });
			}
		});
	}
}
